import { useEffect, useState } from "react";

import { useOverlayDismiss } from "../../lib/useOverlayDismiss";
import { ApiError, apiClient } from "../../api/client";
import type { BlockContributor, BlockDetail, ContributorShareInput, MeResponse } from "../../api/types";
import { UserPicker, type UserPickerValue } from "../../components/UserPicker";
import { hasPermission } from "../../auth/permissions";
import { useIdempotencyKey } from "../../lib/idempotencyKey";
import { useMemberRecipients } from "../../lib/useMemberRecipients";

// ContributorsPanel shows the block's effective contributor splits plus the two
// distinct earning numbers — earned (uncapped accrual) and build (the capped
// Phoenix share) — and, for leaders, opens the split editor (§6.5, §6.7).
export function ContributorsPanel({
  me,
  block,
  onSaved
}: {
  me: MeResponse;
  block: BlockDetail;
  onSaved: () => void;
}) {
  const [open, setOpen] = useState(false);
  // A closed season rejects every write, splits included.
  const canManage = hasPermission(me, "blocks:contributors:manage") && block.season_status !== "closed";

  return (
    <section className="card">
      <h3>Contributors</h3>

      <div className="block-card__tags">
        <span className="chip">Earned: {block.earned.toFixed(1)}</span>
        <span className="chip chip--impact">
          Build: {Math.round(block.build_fraction * 100)}% of size
        </span>
      </div>
      <p className="muted">
        Earned is uncapped accrual; Build is the capped Phoenix share — two different numbers.
      </p>

      {block.contributors.length === 0 ? (
        <p className="muted">No contributors yet.</p>
      ) : (
        <div className="subblock-tree">
          {block.contributors.map((contributor) => (
            <div key={contributor.user.id} className="subblock-row">
              <div className="subblock-row__main">
                <span className="subblock-row__name">
                  {contributor.user.name.trim() === "" ? contributor.user.email : contributor.user.name}
                </span>
              </div>
              <div className="subblock-row__side">
                <span className="chip">{contributor.share_pct}%</span>
                <span className="subblock-row__size">{contributor.earned.toFixed(1)}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {canManage && (
        <button type="button" className="btn btn--primary" onClick={() => setOpen(true)}>
          Edit splits
        </button>
      )}

      <ContributorSplitModal
        open={open}
        block={block}
        onClose={() => setOpen(false)}
        onSaved={() => {
          onSaved();
          setOpen(false);
        }}
      />
    </section>
  );
}

interface SplitRow {
  picker: UserPickerValue;
  share: string;
}

// Seed one editable row per existing contributor; selectedID drives submission
// and query is purely the label the picker shows.
function rowsForBlock(block: BlockDetail): SplitRow[] {
  if (block.contributors.length === 0) {
    return [emptyRow()];
  }
  return block.contributors.map((contributor) => ({
    picker: {
      query: contributor.user.name.trim() === "" ? contributor.user.email : contributor.user.name,
      selectedID: contributor.user.id
    },
    share: String(contributor.share_pct)
  }));
}

function emptyRow(): SplitRow {
  return { picker: { query: "", selectedID: "" }, share: "" };
}

function ContributorSplitModal({
  open,
  block,
  onClose,
  onSaved
}: {
  open: boolean;
  block: BlockDetail;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [rows, setRows] = useState<SplitRow[]>(() => rowsForBlock(block));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Fetch members when the modal opens (the hook is keyed on `open`).
  const membersState = useMemberRecipients(open);
  const { keyFor, reset } = useIdempotencyKey();
  const overlayDismiss = useOverlayDismiss(onClose, submitting);

  // Reset on close so a re-open starts fresh from the current block and the
  // next idempotency key is generated against the reseeded rows.
  useEffect(() => {
    if (!open) {
      setRows(rowsForBlock(block));
      setSubmitting(false);
      setError(null);
      reset();
    }
  }, [open, block, reset]);

  if (!open) {
    return null;
  }

  const total = rows.reduce((sum, row) => {
    const value = Number(row.share);
    return Number.isFinite(value) ? sum + value : sum;
  }, 0);
  const allSelected = rows.length > 0 && rows.every((row) => row.picker.selectedID !== "");
  const sumValid = Math.abs(total - 100) < 0.01;
  const formValid = allSelected && sumValid;

  function setRow(index: number, next: SplitRow) {
    setRows((previous) => previous.map((row, i) => (i === index ? next : row)));
  }

  function removeRow(index: number) {
    setRows((previous) => previous.filter((_, i) => i !== index));
  }

  function addRow() {
    setRows((previous) => [...previous, emptyRow()]);
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!formValid || submitting) {
      return;
    }
    setSubmitting(true);
    setError(null);

    const shares: ContributorShareInput[] = rows.map((row) => ({
      user_id: row.picker.selectedID,
      share_pct: Number(row.share)
    }));

    try {
      await apiClient.setBlockContributors(block.id, shares, keyFor(JSON.stringify(shares)));
      reset();
      onSaved();
    } catch (caught) {
      // The server replays a stored answer for a reused key, so a refused
      // request needs a new key to be retried; a network error may never have
      // reached the server, so that retry keeps the key.
      if (caught instanceof ApiError) {
        reset();
      }
      setError(messageFor(caught));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-overlay" role="presentation" {...overlayDismiss}>
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="contributor-split-title">
        <header className="modal__header">
          <h2 id="contributor-split-title">Edit contributor splits</h2>
          <button
            type="button"
            className="modal__close"
            aria-label="Close"
            onClick={onClose}
            disabled={submitting}
          >
            ×
          </button>
        </header>
        <form className="modal__body" onSubmit={handleSubmit}>
          {rows.map((row, index) => (
            <div key={index} className="form-row form-row--inline split-row">
              <UserPicker
                label="Contributor"
                placeholder="Type a name or email…"
                value={row.picker}
                recipients={membersState.recipients}
                loadStatus={membersState.status}
                loadErrorMessage={membersState.errorMessage}
                loadingMessage="Loading members…"
                emptyMessage="No members in your tenant yet."
                noSelectionHint="Pick a person from the list."
                required
                onChange={(next) => setRow(index, { ...row, picker: next })}
              />
              <label>
                %
                <input
                  type="number"
                  min={0}
                  max={100}
                  step="any"
                  value={row.share}
                  onChange={(event) => setRow(index, { ...row, share: event.target.value })}
                />
              </label>
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => removeRow(index)}
                disabled={submitting}
              >
                Remove
              </button>
            </div>
          ))}

          <button type="button" className="btn btn--ghost" onClick={addRow} disabled={submitting}>
            Add contributor
          </button>

          <p className={sumValid ? "muted" : "form-row__error"}>
            Shares must sum to 100% (currently {Math.round(total * 100) / 100}%)
          </p>

          {error !== null && <p className="form-row__error">{error}</p>}
          <footer className="modal__footer">
            <button type="button" className="btn btn--ghost" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="btn btn--primary" disabled={!formValid || submitting}>
              {submitting ? "Saving…" : "Save splits"}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}

function messageFor(caught: unknown): string {
  if (caught instanceof ApiError) {
    return caught.message;
  }
  return caught instanceof Error ? caught.message : "Couldn't save splits.";
}
